<template>
<v-list dense >
  <v-subheader class="overline" >Checks</v-subheader>
  <v-list-tile >
    <v-list-tile-avatar >
      <v-icon v-if="properties && properties.id" color="success" >check</v-icon>
      <v-icon v-else color="error" >cancel</v-icon>
    </v-list-tile-avatar>
    <v-list-tile-content>
      <v-list-tile-title>Able to fetch form from JotFom</v-list-tile-title>
    </v-list-tile-content>
  </v-list-tile>
  <v-divider/>

  <v-list-tile >
    <v-list-tile-avatar >
      <v-icon v-if="hasWebHook" color="success" >check</v-icon>
      <v-icon v-else color="error" >cancel</v-icon>
    </v-list-tile-avatar>
    <v-list-tile-content>
      <v-list-tile-title>WebHook is installed</v-list-tile-title>
      <v-list-tile-sub-title>The webhook is how JotForm sends submissions to AppointmentGuru</v-list-tile-sub-title>
    </v-list-tile-content>
    <v-list-tile-action v-if="!hasWebHook" >
      <help-popover class='ml-4' >
        <v-subheader class="overline" >The problem</v-subheader>
        Without the webhook installed, JotForm will not send submissions to AppointmentGuru,
        and AppointmentGuru will be unable to connect your form submissions to a client in AppointmentGuru.<br>
        Normally AppointmentGuru will create the webhook for you automatically when you connect your form.
        In this case, it appears that something has gone wrong

        <copy-text-field
          label="Your webhook URL"
          :text-to-copy='webhookUrl' />

        <v-subheader class="overline" >The solution - manually add your webhook</v-subheader>
        <ol>
          <li>Go to your form in jotform</li>
          <li>Click on settings -> integrations</li>
          <li>Search for the "webhook" integration</li>
          <li>Paste the URL above and click "add new webhook"</li>
          <li>Click complete integration</li>
        </ol>

        Return to AppointmentGuru and click the validate form button below

        <p>Not comfortable doing it yourself? Send an email to support@appointmentguru.co and we can help you</p>
      </help-popover>
    </v-list-tile-action>
  </v-list-tile>
  <v-divider/>

  <v-list-tile >
    <v-list-tile-avatar >
      <v-icon v-if="hasRelationshipId" color="success" >check</v-icon>
      <v-icon v-else color="error" >cancel</v-icon>
    </v-list-tile-avatar>
    <v-list-tile-content>
      <v-list-tile-title v-if="hasRelationshipId" >Form has relationship_id</v-list-tile-title>
      <v-list-tile-title v-else >Form is missing a relationship_id field</v-list-tile-title>
      <v-list-tile-sub-title v-if="!hasRelationshipId" class="error--text" >
        <v-icon small color="error" >warning</v-icon>
        Without this your data cannot be linked to a client's profile in AppointmentGuru
      </v-list-tile-sub-title>
      <v-list-tile-sub-title v-else>This is how AppointmentGuru knows which client to connect the form submission to</v-list-tile-sub-title>
    </v-list-tile-content>
    <v-list-tile-action v-if="!hasRelationshipId" >
      <help-popover class='ml-4' >
        <v-subheader class="overline" >The problem</v-subheader>
        In order for this form submission to be able to be linked to the client,
        you need a relationship_id on your form.

        <v-subheader class="overline" >The solution</v-subheader>
        <ol>
          <li>Go to your form in jotform</li>
          <li>Drag a "short text" field onto the form and name it relationship_id</li>
          <li>Click on properties -> advanced: turn on "hide field"</li>
          <li>Click on properties -> advanced: expand "field details" for unique_name, write: relationship_id</li>
          <li>Come back to this page and click on the "revalidate" button (bottom right) to check if you got it right</li>
        </ol>
      </help-popover>
    </v-list-tile-action>
  </v-list-tile>
  <v-divider/>

  <template v-if="properties && properties.activeRedirect === 'thankurl'" >
    <v-list-tile v-if="properties && properties.isEUForm === '1'" >
      <v-list-tile-avatar >
        <v-icon >autorenew</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title>Form redirects:
          <a :href="properties.thankurl" target="_blank" >{{properties.thankurl}}
            <v-icon small >open_in_new</v-icon>
          </a>
        </v-list-tile-title>
        <v-list-tile-sub-title class="error--text"
          v-if="!isValidRedirectUrl(properties)" >
          <v-icon small color="error" >warning</v-icon>
          You are not passing relationship_id to the next form
        </v-list-tile-sub-title>
      </v-list-tile-content>
      <v-list-tile-action v-if="!isValidRedirectUrl(properties)" >
        <help-popover class='ml-4' >
          <v-subheader class="overline" >The problem</v-subheader>
          If you are redirecting this form to another JotForm, you need to pass the relationship_id to the new form
          so that when the client submits that form it can be linked to their profile

          <v-subheader class="overline" >The solution</v-subheader>
          <ol>
            <li>Go to your form in jotform</li>
            <li>Click on settings -> thank you page</li>
            <li>Where it says "enter url" you need to append <code>?relationship_id={relationship_id}</code> to the end of the URL</li>
          </ol>
          <v-subheader class="overline" >Your redirect url should look something like this:</v-subheader>
          <copy-text-field
            :text-to-copy='`${properties.thankurl}?relationship_id={relationship_id}`' />

          When you're done, come back here and click the revalidate form botton to check if you fixed it
        </help-popover>
      </v-list-tile-action>
    </v-list-tile>
  </template>

  <v-list-tile v-if="properties && properties.isEUForm === '1'" >
    <v-list-tile-avatar >
      <v-icon >euro</v-icon>
    </v-list-tile-avatar>
    <v-list-tile-content>
      <v-list-tile-title>Form is stored in the EU</v-list-tile-title>
    </v-list-tile-content>
  </v-list-tile>
</v-list>
</template>
<script>
import HelpPopover from '@/components/ui/HelpPopover'
import CopyTextField from '@/components/ui/CopyTextField'

export default {
  name: 'FormChecks',
  components: { HelpPopover, CopyTextField },
  props: {
    properties: { type: Object, required: true },
    hasRelationshipId: { type: Boolean, required: true },
    webhookKey: { type: String, required: true }
  },
  methods: {
    isValidRedirectUrl (properties) {
      return (properties.thankurl && properties.thankurl.indexOf('?relationship_id={relationship_id}') !== -1)
    }
  },
  computed: {
    webhookUrl () {
      return `https://api.appointmentguru.co/api/v2/practitioner/integrations/jotform/incoming/?key=${this.webhookKey}`
    },
    hasWebHook () {
      let webhooks = this.getObjectPath(this.properties, 'integrations.webhooks.endpoints')
      if (webhooks) {
        let hooks = JSON.parse(webhooks)
        let agHooks = hooks.find((h) => {
          return h === this.webhookUrl
          // let baseIsCorrect = h.startsWith('https://api.appointmentguru.co/api/v2/practitioner/integrations/jotform/incoming/?key=')
          // let hasKey = h.endsWith(this.webhookKey)
          // return (baseIsCorrect && hasKey)
        })
        if (agHooks) { return true }
      }
      return false
    }
  }
}
</script>



// WEBPACK FOOTER //
// FormChecks.vue?ef14bd76